/**
 * @fileoverview Generic local-first reads and chunked requests for department repair jobs.
 * @module system-web/services/departmentRepairJobService
 *
 * Responsibilities:
 * - Read one repair type from the synced `background_jobs` table newest first.
 * - POST reruns in sequential chunks that respect the API's 50-department limit.
 *
 * Dependencies:
 * - @firesquirrel/local-db for synced background job rows.
 * - ../apiClient for online request transport.
 * - ./telemetry for browser observability.
 *
 * Architecture:
 * - Dexie reads are pure and liveQuery-safe; the server owns job writes and claim state.
 * - Requests never optimistically create local rows; sync returns the authoritative jobs.
 *
 * Notes:
 * - Future department repairs reuse this service with their job type and route path.
 */
import { getSystemDexieRaw, type BackgroundJob } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';

/**
 * @contract DepartmentRepairJob
 * Purpose: Generic client view of a synced per-department repair job.
 * Fields: Standard background-job metadata and optional trigger/outcome/lease payload values.
 * Rules: Payload values are optional while a job is pending or running.
 * Sync: Read-only mirror of system Mongo `backgroundJobs`.
 */
export interface DepartmentRepairJob extends Omit<BackgroundJob, 'payload'> {
  payload?: Record<string, unknown> & {
    trigger?: 'startup' | 'manual';
    outcome?: 'repaired' | 'skipped' | 'failed';
    leaseExpiresAt?: string;
    errorType?: string;
  };
}

/**
 * @contract DepartmentRepairRequestResult
 * Purpose: Aggregated response from one or more chunked department repair requests.
 * Fields: Accepted job ids and department ids already active on the server.
 * Rules: Each individual HTTP request contains at most 50 tenant ids.
 * Sync: Accepted jobs arrive through the synced `background_jobs` collection.
 */
export interface DepartmentRepairRequestResult {
  jobs: Array<{ tenantId: string; jobId: string }>;
  alreadyActive: string[];
}

/**
 * @localFirst
 * Source of Truth: Synced `background_jobs` Dexie table.
 * Write Flow: Read-only.
 * Sync Strategy: Standard pull from server-authoritative background jobs.
 *
 * Returns jobs for one repair type, newest first.
 */
export async function listDepartmentRepairJobs(jobType: string): Promise<DepartmentRepairJob[]> {
  const raw = getSystemDexieRaw();
  if (!raw) return [];
  const jobs = await raw.table('background_jobs').toArray() as DepartmentRepairJob[];
  return jobs
    .filter((job) => job.jobType === jobType)
    .sort((left, right) => String(right.startedAt ?? '').localeCompare(String(left.startedAt ?? '')));
}

/**
 * @process requestDepartmentRepair
 * Steps: POST tenant ids in sequential chunks of 50 and aggregate accepted/already-active results.
 * Inputs: Route path and department ObjectId hex strings.
 * Outputs: Aggregated server-created job ids and active tenant ids.
 * Failure Handling: A 409 marks the whole chunk active; other errors include the number already started.
 * Idempotency: REQUIRED — each chunk is claimed atomically by system-api.
 *
 * @localFirst
 * Source of Truth: Server repair claims and synced `background_jobs`.
 * Write Flow: Online POST only; no optimistic Dexie writes.
 * Sync Strategy: Server rows reach the UI through standard pull sync.
 * Conflict Resolution: Server returns already-active ids for a conflicting chunk.
 *
 * @logging
 * Events: department_repair_request_chunk_completed, department_repair_request_chunk_conflict, department_repair_request_failed.
 * Level: info for accepted/conflicting chunks, error for failed requests.
 * Correlation: Route path and chunk counts; department ids are not emitted.
 *
 * @errorHandling
 * Strategy: Continue after 409 chunks; stop after any other error and annotate prior accepted-job count.
 * User Impact: Earlier accepted chunks remain visible even when a later chunk fails.
 * Logging: Telemetry records only fixed route paths, counts, and operation names.
 */
export async function requestDepartmentRepair(
  path: string,
  tenantIds: string[],
): Promise<DepartmentRepairRequestResult> {
  const result: DepartmentRepairRequestResult = { jobs: [], alreadyActive: [] };
  for (let offset = 0; offset < tenantIds.length; offset += 50) {
    const chunk = tenantIds.slice(offset, offset + 50);
    try {
      const response = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify({ tenantIds: chunk }),
      });
      result.jobs.push(...(response?.jobs ?? []));
      result.alreadyActive.push(...(response?.alreadyActive ?? []));
      telemetry.trackEvent('department_repair_request_chunk_completed', {
        path,
        count: String(chunk.length),
        jobCount: String(response?.jobs?.length ?? 0),
      });
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 409) {
        result.alreadyActive.push(...chunk);
        telemetry.trackEvent('department_repair_request_chunk_conflict', {
          path,
          count: String(chunk.length),
        });
        continue;
      }
      telemetry.trackEvent('department_repair_request_failed', {
        path,
        startedCount: String(result.jobs.length),
      });
      telemetry.trackException(
        error instanceof Error ? error : new Error(String(error)),
        { operation: 'requestDepartmentRepair' },
      );
      const originalMessage = error instanceof Error ? error.message : 'Department repair request failed';
      const annotated = new Error(
        `${originalMessage} (${result.jobs.length} departments had already started)`,
      );
      if (error && typeof error === 'object' && 'status' in error) {
        (annotated as Error & { status?: unknown }).status = (error as { status?: unknown }).status;
      }
      throw annotated;
    }
  }
  return result;
}
